import { MS_IN_M, MS_IN_S } from "@/realize-shared/model/Time";
import { useEffect, useState } from "react";
import RIcon from "@/assets/img/icons/animated-r.svg?react";
import Eyecon from "@/assets/img/symbolic/eyeball.svg?react";
import { Phase, type LiveGame, type ProcessedSpectator } from "@/local-shared/model";
import { Icons } from "@/game/Icons";
import OverlappingAvatars from "../shared/OverlappingAvatars";
import { useFullAppContext } from "@/game/AppContext";
import social from "@/api/social";
import type { ClientToServerEvents, ServerToClientEvents } from "@/local-shared/socket-events";
import type { Socket } from "socket.io-client";
import { formatNumberToLetter } from "@/util/Util";
import { motion } from "framer-motion";

function CountDownTimer({
    nextPhase
}: {
    nextPhase: number;
})
{
    const [timeLeft, setTimeLeft] = useState(nextPhase - new Date().getTime());

    useEffect(() =>
    {
        const interval = setInterval(() =>
        {
            const now = new Date().getTime();

            setTimeLeft(nextPhase - now);
        }, 1000);

        return () => void clearInterval(interval);
    }, [nextPhase]);

    const minutes = Math.floor(timeLeft / MS_IN_M);
    const seconds = Math.floor((timeLeft % MS_IN_M) / MS_IN_S);

    return (
        <span
            className={Wind.style({
                fontSize: "text-sm",
                fontWeight: "font-normal",
                lineHeight: "leading-4",
                color: "text-[#3F1413]",
                opacity: "opacity-[0.72]",
                fontFamily: "font-inter",
            }).class}
        >
            {minutes <= 0 ? 0 : minutes}
            :
            {seconds <= 0 ? "00" : seconds.toString().padStart(2, "0")}
        </span>
    );
}

const ANIMATION_TIMING = 0.3;

export default function LiveGameSlot({
    gameData,
    socket,
    gameIndex,
    spectateGame,
}: {
    gameData: LiveGame;
    socket: Socket<ServerToClientEvents, ClientToServerEvents>;
    gameIndex: number;
    spectateGame: (roomID: string) => void;
})
{
    const appContext = useFullAppContext();

    const [spectators, setSpectators] = useState<ProcessedSpectator[]>([]);


    useEffect(() =>
    {
        const fetchSpectators = async () =>
        {
            try
            {
                const response = await social.rooms.getSpectators(gameData.roomID);

                setSpectators(response.spectators);
            }
            catch (error)
            {
                Logger.error("Failed to fetch spectators:", error);
            }
        };

        fetchSpectators().catch((e) => void Logger.error(e));
    }, [gameData.roomID, appContext]);

    useEffect(() =>
    {
        const handleSpectatorList = (newSpectators: ProcessedSpectator[]) =>
        {
            Logger.log("Received spectator list:", newSpectators);
            setSpectators(newSpectators);
        };

        socket.emit("spectateRoom", gameData.roomID);

        socket.on("spectatorList", handleSpectatorList);

        return () =>
        {
            socket.emit("stopSpectating", gameData.roomID);
            socket.removeListener("spectatorList", handleSpectatorList);
        };
    }, [gameData.roomID, socket]);

    const getPlatform = () =>
    {
        return (
            <RIcon
                className={Wind.style({
                    width: "w-8",
                    height: "h-8",
                }).class}
            />
        );
    };

    const getStatusText = (phase: Phase) =>
    {
        switch (phase)
        {
            case Phase.LOBBY:
                return "Lobby";
            case Phase.HEADLINE_CREATION:
            case Phase.PHOTO_SUBMISSION:
            case Phase.VOTING:
                return `Round ${gameData.currentRound}`;
            case Phase.CLOSED:
                return "Closed";
            default:
                return "Unknown Phase";
        }
    };

    if (gameData.timeLeft - Date.now() <= 0)
    {
        return null; // Don't render if the game is closed or time is up
    }


    return (
        <motion.div
            className={Wind.style({
                backgroundColor: "bg-[#FFE0A4]",
                borderRadius: "rounded-2xl",
                paddingX: "px-3",
                paddingY: "py-2",
                display: "inline-flex",
                minHeight: "min-h-20",
                minWidth: "min-w-[10rem]",
                maxWidth: "max-w-52",
                flexDirection: "flex-col",
            }).class}
            onClick={() =>
            {
                // Logger.log("Specate Game", gameData.roomID);

                // localStorage.setItem("activeTab", "search" as TabName);

                // appContext.spectateRoom(gameData.roomID);
                spectateGame(gameData.roomID);
            }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: ANIMATION_TIMING, delay: (ANIMATION_TIMING / 2) * gameIndex }}
        >
            <div
                className={Wind.style({
                    display: "flex",
                    flexDirection: "flex-row",
                    gap: "gap-4",
                    height: "h-full",
                }).class}
            >
                <div
                    className={Wind.style({
                        height: "h-full",
                        display: "flex",
                        alignItems: "items-center",
                    }).class}
                >
                    {getPlatform()}
                </div>
                <div
                    className={Wind.style({
                        flexGrow: "grow",
                    }).class}
                >
                    <h1
                        className={Wind.style({
                            fontSize: "text-base",
                            fontWeight: "font-semibold",
                            lineHeight: "leading-5",
                            color: "text-[#3F1413]",
                            textOverflow : "text-ellipsis",
                            whitespace: "whitespace-nowrap",
                            overflow: "overflow-hidden",
                            marginBottom: "mb-1",
                            fontFamily: "font-inter",
                        }).class}
                    >
                        {gameData.hostName}
                    </h1>
                    <div
                        className={Wind.style({
                            display: "flex",
                            flexDirection: "flex-row",
                            alignItems: "items-center",
                            justifyContent: "justify-between",
                            marginBottom: "mb-1",
                            flexGrow: "grow",
                        }).class}
                    >
                        <span
                            className={Wind.style({
                                fontFamily: "font-inter",
                                fontSize: "text-sm",
                                fontWeight: "font-medium",
                                lineHeight: "leading-4",
                                color: "text-[#3F1413]",
                                whitespace: "whitespace-nowrap",
                            }).class}
                        >
                            {getStatusText(gameData.phase)}
                        </span>
                        <CountDownTimer
                            nextPhase={gameData.timeLeft}
                        />
                    </div>
                    <div
                        className={Wind.style({
                            display: "flex",
                            flexDirection: "flex-row",
                            alignItems: "items-center",
                            justifyContent: "justify-between",
                            gap: "gap-2",
                        }).class}
                    >
                        <div
                            className={Wind.style({
                                display: "inline-flex",
                                flexDirection: "flex-row",
                                alignItems: "items-center",
                                gap: "gap-1",
                                height: "h-8",
                                backgroundColor: "bg-[#FFEFD3]",
                                paddingY: "py-0.5",
                                paddingLeft: "pl-0.5",
                                borderRadius: "rounded-full",
                            }).class}
                        >
                            <span
                                className={Wind.style({
                                    fontSize: "text-base",
                                    fontWeight: "font-medium",
                                    lineHeight: "leading-6",
                                    letterSpacing: "tracking-[0%]",
                                    color: "text-[#3F1413]",
                                    fontFamily: "font-poppins",
                                    textAlign: "text-center",
                                    width: "w-7",
                                    borderRadius: "rounded-full",
                                    backgroundColor: "bg-[#FFEFD3]",
                                }).class}
                            >
                                {gameData.players.length}
                            </span>
                            <OverlappingAvatars
                                avatars={gameData.players.map((p) => Icons.get(p.avatar).path)}
                                maxAvatars={3}
                            />
                        </div>
                        <div
                            className={Wind.style({
                                display: "inline-flex",
                                flexDirection: "flex-row",
                                alignItems: "items-center",
                                gap: "gap-1",
                            }).class}
                        >
                            <Eyecon
                                className={Wind.style({
                                    width: "w-4",
                                    height: "h-4",
                                    color: "text-dark-blue",
                                }).class}
                            />
                            <span>
                                {formatNumberToLetter(spectators.length)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
